/**
 * NTF — co-located TanStack Query hooks. Query keys are namespaced under 'ntf'
 * and scoped by workspace where the data is workspace-tier, so cache entries
 * don't leak across entered workspaces.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ntfApi } from './api';
import type {
  CreateTemplateInput,
  DeliveryLogQuery,
  NotificationQuery,
  PreviewInput,
  TemplateListQuery,
  UpdateSlaInput,
  UpdateTemplateInput,
} from './types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const ntfKeys = {
  templates: (ws: string, q: TemplateListQuery) =>
    ['ntf', 'templates', ws, q] as const,
  template: (ws: string, id: string) => ['ntf', 'template', ws, id] as const,
  templateVersions: (ws: string, id: string) =>
    ['ntf', 'templateVersions', ws, id] as const,
  mergeVars: (ws: string, scope?: string) =>
    ['ntf', 'mergeVars', ws, scope ?? 'all'] as const,
  sla: (ws: string) => ['ntf', 'sla', ws] as const,
  notifications: (q: NotificationQuery) => ['ntf', 'notifications', q] as const,
  unreadCount: () => ['ntf', 'unreadCount'] as const,
  deliveryLog: (q: DeliveryLogQuery) => ['ntf', 'deliveryLog', q] as const,
};

// ---------------------------------------------------------------------------
// Page 1 — Templates
// ---------------------------------------------------------------------------
export function useTemplates(ws: string, q: TemplateListQuery) {
  return useQuery({
    queryKey: ntfKeys.templates(ws, q),
    queryFn: () => ntfApi.listTemplates(q),
  });
}

export function useTemplate(ws: string, id: string | null) {
  return useQuery({
    queryKey: ntfKeys.template(ws, id ?? '∅'),
    queryFn: () => ntfApi.getTemplate(id as string),
    enabled: !!id,
  });
}

export function useTemplateVersions(ws: string, id: string | null, enabled = true) {
  return useQuery({
    queryKey: ntfKeys.templateVersions(ws, id ?? '∅'),
    queryFn: () => ntfApi.listVersions(id as string),
    enabled: !!id && enabled,
  });
}

export function useMergeVars(ws: string, scope?: 'person' | 'company') {
  return useQuery({
    queryKey: ntfKeys.mergeVars(ws, scope),
    queryFn: () => ntfApi.listMergeVars(scope),
  });
}

export function useCreateTemplate(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateInput) => ntfApi.createTemplate(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'templates', ws] });
    },
  });
}

export function useUpdateTemplate(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTemplateInput }) =>
      ntfApi.updateTemplate(id, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'templates', ws] });
      void qc.invalidateQueries({ queryKey: ntfKeys.template(ws, data.id) });
      void qc.invalidateQueries({ queryKey: ntfKeys.templateVersions(ws, data.id) });
    },
  });
}

export function useArchiveTemplate(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      ntfApi.archiveTemplate(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'templates', ws] });
      void qc.invalidateQueries({ queryKey: ['ntf', 'template', ws] });
    },
  });
}

export function useRestoreTemplate(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      ntfApi.restoreTemplate(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'templates', ws] });
      void qc.invalidateQueries({ queryKey: ['ntf', 'template', ws] });
    },
  });
}

export function usePreview() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PreviewInput }) =>
      ntfApi.preview(id, body),
  });
}

// ---------------------------------------------------------------------------
// Page 2 — SLAs
// ---------------------------------------------------------------------------
export function useSlaSettings(ws: string) {
  return useQuery({
    queryKey: ntfKeys.sla(ws),
    queryFn: () => ntfApi.getSlaSettings(),
  });
}

export function useUpdateSlaSettings(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSlaInput) => ntfApi.updateSlaSettings(body),
    onSuccess: (data) => {
      qc.setQueryData(ntfKeys.sla(ws), data);
    },
  });
}

// ---------------------------------------------------------------------------
// Page 3 — Notifications feed
// ---------------------------------------------------------------------------
export function useNotifications(q: NotificationQuery, enabled = true) {
  return useQuery({
    queryKey: ntfKeys.notifications(q),
    queryFn: () => ntfApi.listNotifications(q),
    enabled,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ntfKeys.unreadCount(),
    queryFn: () => ntfApi.unreadCount(),
    // Keep the bell badge reasonably fresh without hammering the API.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version?: number }) =>
      ntfApi.markRead(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'notifications'] });
      void qc.invalidateQueries({ queryKey: ntfKeys.unreadCount() });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ntfApi.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ntf', 'notifications'] });
      void qc.invalidateQueries({ queryKey: ntfKeys.unreadCount() });
    },
  });
}

// ---------------------------------------------------------------------------
// Page 4 — Transactional Delivery Log
// ---------------------------------------------------------------------------
export function useDeliveryLog(q: DeliveryLogQuery) {
  return useQuery({
    queryKey: ntfKeys.deliveryLog(q),
    queryFn: () => ntfApi.listDeliveryLog(q),
  });
}
